import * as knowledgeApi from '@/api/knowledgeApi';

export type { KnowledgeSearchHit, KnowledgeSearchRequest } from '@/api/knowledgeApi';

/** 与 ai-service 检索策略对齐：普通成员不向模型透出含敏感经营词的整段文本 */
const SENSITIVE_SNIPPET_MARKERS = [
  '费用',
  '成本',
  '供应链',
  '利润',
  '净利润',
  '毛利率',
  '营收',
  '预算',
  '毛利',
  '净利',
  '合同额',
  '现金流',
  '盈亏',
  '采购价',
  '报价',
];

export function sanitizeSensitiveProjectKnowledgeText(text: string): string {
  const s = String(text ?? '');
  if (!s.trim()) return s;
  if (SENSITIVE_SNIPPET_MARKERS.some((k) => s.includes(k))) {
    return '【已按权限隐藏与费用/成本/供应链/利润等相关的检索片段】';
  }
  return s;
}

/**
 * 仅返回命中列表，适合列表页、下拉联想等场景。
 */
export async function searchKnowledgeHits(
  query: string,
  options?: Omit<knowledgeApi.KnowledgeSearchRequest, 'query'>,
): Promise<knowledgeApi.KnowledgeSearchHit[]> {
  const r = await knowledgeApi.searchKnowledgeStructured({
    query,
    ...options,
  });
  return r.hits ?? [];
}

/**
 * 返回完整检索结果（含 rawData），便于将片段拼进 Agent / WBS 等上下文。
 */
export async function searchKnowledgeFull(
  query: string,
  options?: Omit<knowledgeApi.KnowledgeSearchRequest, 'query'>,
): Promise<knowledgeApi.KnowledgeSearchResult> {
  return knowledgeApi.searchKnowledgeStructured({ query, ...options });
}

/**
 * 将命中格式化为可读文本块，便于直接拼接到 prompt。
 */
export function formatKnowledgeHitsForPrompt(
  hits: knowledgeApi.KnowledgeSearchHit[],
  opts?: { maxItems?: number; maxSnippetChars?: number; sanitizeFinancialSensitive?: boolean },
): string {
  const maxItems = opts?.maxItems ?? 8;
  const maxSnippetChars = opts?.maxSnippetChars ?? 1200;
  const sanitize = opts?.sanitizeFinancialSensitive === true;
  const lines: string[] = [];
  let used = 0;
  for (const h of hits.slice(0, maxItems)) {
    let title = h.title || h.document_id || h.documentId || '（无标题）';
    const src = h.source ? `来源:${h.source}` : '';
    const score = h.score != null ? `相关度:${Number(h.score).toFixed(3)}` : '';
    let snip = (h.snippet || '').replace(/\s+/g, ' ').trim();
    if (sanitize) {
      title = sanitizeSensitiveProjectKnowledgeText(title);
      snip = sanitizeSensitiveProjectKnowledgeText(snip);
    }
    if (snip.length > maxSnippetChars) snip = `${snip.slice(0, maxSnippetChars)}…`;
    const head = [title, src, score].filter(Boolean).join(' | ');
    lines.push(`- ${head}${snip ? `\n  ${snip}` : ''}`);
    used += snip.length + head.length;
    if (used > maxSnippetChars * maxItems) break;
  }
  return lines.length ? `【知识库检索】\n${lines.join('\n')}` : '';
}
